// Localização: frontend/src/components/Financeiro/ListaContasPagar.jsx
// Componente para listar contas a pagar com filtros e ações

import React, { useState } from 'react';
import {
    Edit,
    Trash2,
    DollarSign,
    Calendar,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Clock
} from 'lucide-react';
import LoadingIndicator from '../Common/LoadingIndicator';
import ConfirmacaoModal from '../Common/ConfirmacaoModal';
import ResumoFinanceiro from './ResumoFinanceiro';

const ListaContasPagar = ({
    contasPagar,
    isLoading,
    onEdit,
    onDelete,
    onPagar,
    canEdit,
    canDelete
}) => {
    const [contaParaDeletar, setContaParaDeletar] = useState(null);

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const formatarData = (data) => {
        if (!data) return '-';
        return new Date(data).toLocaleDateString('pt-BR');
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'Pendente': {
                icon: Clock,
                bg: 'bg-yellow-100',
                text: 'text-yellow-800',
                label: 'Pendente'
            },
            'Pago': {
                icon: CheckCircle2,
                bg: 'bg-green-100',
                text: 'text-green-800',
                label: 'Pago'
            },
            'Vencido': {
                icon: AlertCircle,
                bg: 'bg-red-100',
                text: 'text-red-800',
                label: 'Atrasado'
            },
            'Cancelado': {
                icon: XCircle,
                bg: 'bg-gray-100',
                text: 'text-gray-800',
                label: 'Cancelado'
            }
        };

        const config = statusConfig[status] || statusConfig['Pendente'];
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                <Icon className="h-3 w-3" />
                {config.label}
            </span>
        );
    };

    const handleConfirmarDelete = async () => {
        if (contaParaDeletar) {
            await onDelete(contaParaDeletar.id);
            setContaParaDeletar(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <LoadingIndicator />
            </div>
        );
    }

    return (
        <>
            {/* Resumo Estatístico */}
            <ResumoFinanceiro contas={contasPagar} tipo="pagar" />

            {/* Tabela de Contas (Desktop) */}
            {contasPagar.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Nenhuma conta a pagar encontrada.</p>
                </div>
            ) : (
                <>
                    {/* Desktop View */}
                    <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Descrição
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Fornecedor
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Categoria
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Vencimento
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Valor Total
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Valor Pendente
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {contasPagar.map((conta) => (
                                        <tr key={conta.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {conta.descricao}
                                                </div>
                                                {conta.observacoes && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {conta.observacoes.substring(0, 50)}
                                                        {conta.observacoes.length > 50 ? '...' : ''}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {conta.cliente_fornecedor || '-'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {conta.categoria?.nome || '-'}
                                                </div>
                                                {conta.subcategoria && (
                                                    <div className="text-xs text-gray-500">
                                                        {conta.subcategoria.nome}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-1 text-sm text-gray-900">
                                                    <Calendar className="h-3 w-3 text-gray-400" />
                                                    {formatarData(conta.data_vencimento)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                {formatarMoeda(conta.valor_total)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-orange-600">
                                                {formatarMoeda(conta.valor_pendente)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(conta.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-2">
                                                    {conta.status !== 'Pago' && conta.status !== 'Cancelado' && canEdit && (
                                                        <button
                                                            onClick={() => onPagar(conta)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium"
                                                            title="Pagar"
                                                        >
                                                            <DollarSign className="h-3 w-3" />
                                                            Pagar
                                                        </button>
                                                    )}
                                                    {conta.status !== 'Pago' && canEdit && (
                                                        <button
                                                            onClick={() => onEdit(conta)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    {conta.status !== 'Pago' && canDelete && (
                                                        <button
                                                            onClick={() => setContaParaDeletar(conta)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title={conta.status === 'Cancelado' ? 'Confirmar Cancelamento' : 'Cancelar'}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile View */}
                    <div className="md:hidden space-y-4">
                        {contasPagar.map((conta) => (
                            <div key={conta.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900">{conta.descricao}</h3>
                                        <p className="text-xs text-gray-500">{conta.cliente_fornecedor}</p>
                                    </div>
                                    {getStatusBadge(conta.status)}
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                                    <div>
                                        <p className="text-gray-500 text-xs">Vencimento</p>
                                        <div className="flex items-center gap-1 font-medium text-gray-900">
                                            <Calendar className="h-3 w-3 text-gray-400" />
                                            {formatarData(conta.data_vencimento)}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs">Categoria</p>
                                        <p className="font-medium text-gray-900">{conta.categoria?.nome || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs">Valor Total</p>
                                        <p className="font-semibold text-gray-900">{formatarMoeda(conta.valor_total)}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-xs">Pendente</p>
                                        <p className="font-semibold text-orange-600">{formatarMoeda(conta.valor_pendente)}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                                    {conta.status !== 'Pago' && conta.status !== 'Cancelado' && canEdit && (
                                        <button
                                            onClick={() => onPagar(conta)}
                                            className="flex-1 inline-flex justify-center items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                        >
                                            <DollarSign className="h-4 w-4" />
                                            Pagar
                                        </button>
                                    )}
                                    <div className="flex gap-2">
                                        {conta.status !== 'Pago' && canEdit && (
                                            <button
                                                onClick={() => onEdit(conta)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100"
                                                title="Editar"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                        )}
                                        {conta.status !== 'Pago' && canDelete && (
                                            <button
                                                onClick={() => setContaParaDeletar(conta)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100"
                                                title={conta.status === 'Cancelado' ? 'Confirmar Cancelamento' : 'Cancelar'}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Modal de confirmação de exclusão */}
            <ConfirmacaoModal
                isOpen={!!contaParaDeletar}
                onClose={() => setContaParaDeletar(null)}
                onConfirm={handleConfirmarDelete}
                title="Cancelar Conta a Pagar"
                message={`Tem certeza que deseja cancelar a conta "${contaParaDeletar?.descricao}"? Esta ação não pode ser desfeita.`}
                confirmText="Cancelar Conta"
                cancelText="Voltar"
            />
        </>
    );
};

export default ListaContasPagar;
